import { useState } from 'react';

import { CheckCircle, Close, Edit } from '@mui/icons-material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

type EditableTitleProps = {
	name: string;
	onNameChanged: (name: string) => void;
};

export function EditableTitle({ name, onNameChanged }: EditableTitleProps) {
	const [editName, setEditName] = useState<string>(name);
	const [editMode, setEditMode] = useState(false);

	const leaveEditMode = (applyChange: boolean) => {
		if (applyChange) {
			onNameChanged(editName);
		}
		setEditMode(false);
	};

	if (!editMode) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
				<Typography
					color="textSecondary"
					noWrap
					onClick={() => {
						setEditName(name);
						setEditMode(true);
					}}
					sx={{
						minWidth: 0,
						cursor: 'pointer',
						fontSize: '1rem',
						lineHeight: 1.4375,
						letterSpacing: '0.00938em',
					}}
				>
					{name}
				</Typography>
				<IconButton
					color="default"
					aria-label="edit name"
					size="small"
					onClick={() => {
						setEditName(name);
						setEditMode(true);
					}}
					sx={{ flexShrink: 0, ml: 0.5 }}
				>
					<Edit fontSize="small" />
				</IconButton>
			</Box>
		);
	}

	return (
		<Typography variant="h4" color="textSecondary" style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
			<TextField
				variant="standard"
				value={editName}
				onChange={(e) => setEditName(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						leaveEditMode(true);
					} else if (e.key === 'Escape') {
						leaveEditMode(false);
					}
				}}
				onBlur={() => {
					if (editMode) {
						leaveEditMode(false);
					}
				}}
				slotProps={{
					input: {
						disableUnderline: false,
						endAdornment: (
							<InputAdornment position="end">
								<IconButton size="small" aria-label="apply edit" onMouseDown={(e) => e.preventDefault()} onClick={() => leaveEditMode(true)}>
									<CheckCircle fontSize="small" />
								</IconButton>
								<IconButton size="small" aria-label="cancel edit" onMouseDown={(e) => e.preventDefault()} onClick={() => leaveEditMode(false)}>
									<Close fontSize="small" />
								</IconButton>
							</InputAdornment>
						),
					},
				}}
				sx={{
					flexGrow: 1,
				}}
			/>
		</Typography>
	);
}
